import type { SkillConfig, WaveConfig } from "@bastion/data";
import type { EnemyState } from "../types";
import type { ResolvedMap } from "../repos/ConfigRepository";
import { computeGlobalShortestPath } from "./movement";

/** 纯波次生成调度。 */
export interface SpawnPlan {
  enemyType: string;
  spawnTime: number;
}

/** 计算半开区间 [prevTime, prevTime + dt] 内到期的生成计划。 */
export function planSpawns(
  wave: WaveConfig,
  waveStartTime: number,
  prevTime: number,
  dt: number,
): SpawnPlan[] {
  const nextTime = prevTime + dt;
  const plans: SpawnPlan[] = [];
  for (const group of wave.groups) {
    for (let i = 0; i < group.count; i++) {
      const spawnTime = waveStartTime + group.delay + i * group.spawnInterval;
      const dueAtStart = prevTime === waveStartTime && spawnTime === waveStartTime;
      if ((spawnTime > prevTime || dueAtStart) && spawnTime <= nextTime) {
        plans.push({ enemyType: group.enemyId, spawnTime });
      }
    }
  }
  plans.sort((a, b) => a.spawnTime - b.spawnTime || a.enemyType.localeCompare(b.enemyType));
  return plans;
}

/** 使用全局出生点到基地的路径构建一个敌人。 */
export function buildSpawningEnemy(
  plan: SpawnPlan,
  spawnPoint: { x: number; y: number },
  baseConfig: {
    stats: {
      maxHp: number;
      attack: number;
      magic: number;
      defense: number;
      magicResistance: number;
      speed: number;
    };
    isBoss?: boolean;
    skills: SkillConfig[];
  },
  waveIndex: number,
  entityCounter: number,
  resolvedMap: ResolvedMap,
  towers: { id: string; position: { x: number; y: number } }[],
): (Omit<EnemyState, "id"> & { id: string }) | null {
  const path = computeGlobalShortestPath(
    spawnPoint,
    resolvedMap.map.base,
    towers.map((tower) => tower.position),
    resolvedMap,
  );
  if (!path || path.length < 2) return null;

  const hp = baseConfig.stats.maxHp;
  return {
    id: `enemy-${entityCounter}`,
    type: plan.enemyType,
    hp,
    maxHp: hp,
    attack: baseConfig.stats.attack,
    magic: baseConfig.stats.magic,
    defense: baseConfig.stats.defense,
    magicResistance: baseConfig.stats.magicResistance,
    speed: baseConfig.stats.speed,
    waveIndex,
    position: { ...path[0] },
    waypoints: path,
    currentWaypointIndex: 0,
    segmentProgress: 0,
    alive: true,
    markedDead: false,
    isBoss: baseConfig.isBoss === true,
    statusEffects: [],
    skillTimers: baseConfig.skills.reduce<Record<string, number>>((timers, skill) => {
      if (skill.type === "MONSTER" && skill.releaseCondition?.kind === "interval") {
        timers[skill.id] = skill.releaseCondition.initialDelay ?? skill.releaseCondition.seconds;
      }
      return timers;
    }, {}),
    regenTimer: 0,
  };
}
